// ============================================================
// 天水數位店長 — 門店設定頁
// 打卡規則（模式/遲到/早退/加班/休息）+ 門市資訊 + 管理人員
// 勞基法 §30, §32, §35, §36 合規
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { LawParamsPanel } from './LawParamsPanel';

type SideTab = 'clockRules' | 'shopInfo' | 'managers' | 'law';
type ClockSubTab = 'mode' | 'late' | 'earlyLeave' | 'overtime' | 'break';

interface Settings {
  attendanceMethods: string[];
  qrRefreshSeconds: number;
  allowedWifiSSID: string | null;
  geoFenceLat: number | null;
  geoFenceLng: number | null;
  geoFenceRadiusM: number | null;
  lateGraceMinutes: number;
  lateDeductEnabled: boolean;
  lateDeductUnit: number;
  lateDeductAmount: number;
  earlyLeaveGraceMinutes: number;
  earlyLeaveDeductEnabled: boolean;
  earlyLeaveDeductUnit: number;
  earlyLeaveDeductAmount: number;
  overtimeRequiresApproval: boolean;
  overtimeMaxMonthly: number;
  overtimeToCompLeave: boolean;
  compLeaveDeadlineDays: number;
  breakClockEnabled: boolean;
  breakDurationMinutes: number;
  shopAddress: string | null;
  shopPhone: string | null;
  businessHours: string | null;
  tenant: { name: string; slug: string; plan: string } | null;
}

interface Manager {
  id: string;
  name: string;
  email: string;
  role: string;
}

const defaultSettings: Settings = {
  attendanceMethods: ['QR'], qrRefreshSeconds: 15, allowedWifiSSID: null,
  geoFenceLat: null, geoFenceLng: null, geoFenceRadiusM: null,
  lateGraceMinutes: 5, lateDeductEnabled: false, lateDeductUnit: 30, lateDeductAmount: 0,
  earlyLeaveGraceMinutes: 5, earlyLeaveDeductEnabled: false, earlyLeaveDeductUnit: 30, earlyLeaveDeductAmount: 0,
  overtimeRequiresApproval: true, overtimeMaxMonthly: 46, overtimeToCompLeave: true, compLeaveDeadlineDays: 180,
  breakClockEnabled: false, breakDurationMinutes: 60,
  shopAddress: null, shopPhone: null, businessHours: null, tenant: null,
};

export function SettingsPage() {
  const { user } = useAuthStore();
  const isOwner = user?.role === 'OWNER';

  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [sideTab, setSideTab] = useState<SideTab>('clockRules');
  const [clockSubTab, setClockSubTab] = useState<ClockSubTab>('mode');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  // 店內打卡站啟用碼（只顯示在老闆自己的畫面上，10 分鐘有效、只能用一次）
  const [kioskCode, setKioskCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [kioskCodeError, setKioskCodeError] = useState<string | null>(null);

  const generateKioskCode = async () => {
    setKioskCodeError(null);
    try {
      setKioskCode(await api.post<{ code: string; expiresAt: string }>('/kiosk/activation-code'));
    } catch (e) {
      setKioskCodeError((e as Error).message);
    }
  };

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ settings: Settings }>('/settings');
      setSettings(data.settings);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const fetchManagers = useCallback(async () => {
    try {
      const data = await api.get<{ managers: Manager[] }>('/settings/managers');
      setManagers(data.managers);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchSettings(); fetchManagers(); }, [fetchSettings, fetchManagers]);

  const save = async (patch: Partial<Settings>) => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/settings', patch);
      setSettings(prev => ({ ...prev, ...patch }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggleMethod = (method: string) => {
    const current = settings.attendanceMethods;
    const next = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];
    if (next.length === 0) return; // 至少一種
    save({ attendanceMethods: next });
  };

  const toggleManagerRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'MANAGER' ? 'STAFF' : 'MANAGER';
    try {
      await api.put(`/settings/managers/${userId}`, { role: newRole });
      fetchManagers();
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const sideItems: { key: SideTab; label: string }[] = [
    { key: 'clockRules', label: '打卡規則' },
    { key: 'shopInfo', label: '門市資訊' },
    { key: 'managers', label: '門市管理人員' },
    { key: 'law', label: '法規數字' },
  ];

  const clockTabs: { key: ClockSubTab; label: string }[] = [
    { key: 'mode', label: '打卡模式' },
    { key: 'late', label: '遲到設定' },
    { key: 'earlyLeave', label: '早退設定' },
    { key: 'overtime', label: '加班設定' },
    { key: 'break', label: '休息時間打卡' },
  ];

  if (loading) {
    return <div className="min-h-screen bg-morandi-50 flex items-center justify-center text-morandi-300">載入設定中...</div>;
  }

  return (
    <div className="min-h-screen bg-morandi-50 p-4 pb-24">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-morandi-800 tracking-tight">門店設定</h1>
      </header>

      {/* 手機：分頁排在上方一列；平板／電腦：維持左側欄。
          原本一律左側欄，手機上內容被擠成約 130px 寬的一條，整頁橫向溢出。 */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Left sidebar */}
        <div className="md:w-48 md:flex-shrink-0">
          <nav className="flex gap-1 overflow-x-auto md:block md:space-y-1">
            {sideItems.map(item => (
              <button key={item.key}
                onClick={() => setSideTab(item.key)}
                className={`whitespace-nowrap md:w-full text-left px-4 py-3 rounded-btn text-sm font-medium transition-colors
                  ${sideTab === item.key ? 'bg-white shadow-card text-morandi-800' : 'text-morandi-400 hover:text-morandi-600 hover:bg-morandi-100'}`}>
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* ── 打卡規則 ──────────────────────────────── */}
          {sideTab === 'clockRules' && (
            <div className="bg-white rounded-card shadow-card">
              {/* Sub tabs */}
              <div className="flex border-b border-morandi-100 overflow-x-auto">
                {clockTabs.map(tab => (
                  <button key={tab.key}
                    onClick={() => setClockSubTab(tab.key)}
                    className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2
                      ${clockSubTab === tab.key ? 'border-earth-400 text-earth-600' : 'border-transparent text-morandi-400 hover:text-morandi-600'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* 打卡模式 */}
                {clockSubTab === 'mode' && (
                  <div>
                    <div className="flex gap-6 mb-6">
                      {(['GPS', 'WIFI', 'QR'] as const).map(method => (
                        <label key={method} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox"
                            checked={settings.attendanceMethods.includes(method)}
                            onChange={() => toggleMethod(method)}
                            className="w-5 h-5 rounded border-morandi-300 text-earth-500 focus:ring-earth-400" />
                          <span className="text-sm text-morandi-700">
                            {method === 'QR' ? 'QR Code' : method}
                          </span>
                        </label>
                      ))}
                    </div>

                    {settings.attendanceMethods.includes('WIFI') && (
                      <FieldRow label="WiFi SSID" hint="員工手機需連到此 WiFi 才能打卡">
                        <input value={settings.allowedWifiSSID || ''} onChange={e => setSettings(s => ({ ...s, allowedWifiSSID: e.target.value }))}
                          onBlur={() => save({ allowedWifiSSID: settings.allowedWifiSSID })}
                          className="input-field" placeholder="例：Tianshui-Staff" />
                      </FieldRow>
                    )}

                    {settings.attendanceMethods.includes('GPS') && (
                      <>
                        <FieldRow label="GPS 圍欄中心（緯度）">
                          <input type="number" step="0.0001" value={settings.geoFenceLat ?? ''} className="input-field"
                            onChange={e => setSettings(s => ({ ...s, geoFenceLat: e.target.value ? Number(e.target.value) : null }))}
                            onBlur={() => save({ geoFenceLat: settings.geoFenceLat })} />
                        </FieldRow>
                        <FieldRow label="GPS 圍欄中心（經度）">
                          <input type="number" step="0.0001" value={settings.geoFenceLng ?? ''} className="input-field"
                            onChange={e => setSettings(s => ({ ...s, geoFenceLng: e.target.value ? Number(e.target.value) : null }))}
                            onBlur={() => save({ geoFenceLng: settings.geoFenceLng })} />
                        </FieldRow>
                        <FieldRow label="圍欄半徑（公尺）">
                          <input type="number" value={settings.geoFenceRadiusM ?? ''} className="input-field"
                            onChange={e => setSettings(s => ({ ...s, geoFenceRadiusM: e.target.value ? Number(e.target.value) : null }))}
                            onBlur={() => save({ geoFenceRadiusM: settings.geoFenceRadiusM })} />
                        </FieldRow>
                      </>
                    )}

                    {settings.attendanceMethods.includes('QR') && (
                      <FieldRow label="QR Code 刷新秒數" hint="建議 15~30 秒">
                        <input type="number" min={5} max={60} value={settings.qrRefreshSeconds} className="input-field w-24"
                          onChange={e => setSettings(s => ({ ...s, qrRefreshSeconds: Number(e.target.value) }))}
                          onBlur={() => save({ qrRefreshSeconds: settings.qrRefreshSeconds })} />
                      </FieldRow>
                    )}

                    <LawNote>打卡模式決定員工上下班的驗證方式。可同時啟用多種方式，員工打卡時擇一使用。</LawNote>
                  </div>
                )}

                {/* 遲到設定 */}
                {clockSubTab === 'late' && (
                  <div>
                    <FieldRow label="遲到容許時間（分鐘）" hint="超過此時間計為遲到">
                      <input type="number" min={0} max={30} value={settings.lateGraceMinutes} className="input-field w-24"
                        onChange={e => setSettings(s => ({ ...s, lateGraceMinutes: Number(e.target.value) }))}
                        onBlur={() => save({ lateGraceMinutes: settings.lateGraceMinutes })} />
                    </FieldRow>

                    <FieldRow label="啟用遲到扣薪">
                      <Toggle checked={settings.lateDeductEnabled}
                        onChange={v => { setSettings(s => ({ ...s, lateDeductEnabled: v })); save({ lateDeductEnabled: v }); }} />
                    </FieldRow>

                    {settings.lateDeductEnabled && (
                      <>
                        <FieldRow label="扣薪單位（每 N 分鐘）">
                          <input type="number" min={1} value={settings.lateDeductUnit} className="input-field w-24"
                            onChange={e => setSettings(s => ({ ...s, lateDeductUnit: Number(e.target.value) }))}
                            onBlur={() => save({ lateDeductUnit: settings.lateDeductUnit })} />
                        </FieldRow>
                        <FieldRow label="每單位扣薪金額（$）">
                          <input type="number" min={0} value={settings.lateDeductAmount} className="input-field w-24"
                            onChange={e => setSettings(s => ({ ...s, lateDeductAmount: Number(e.target.value) }))}
                            onBlur={() => save({ lateDeductAmount: settings.lateDeductAmount })} />
                        </FieldRow>
                      </>
                    )}

                    <LawNote>勞基法 §30：正常工時以出勤表為準。遲到扣薪需於工作規則或勞動契約中明定，且扣款不得超過當日工資。</LawNote>
                  </div>
                )}

                {/* 早退設定 */}
                {clockSubTab === 'earlyLeave' && (
                  <div>
                    <FieldRow label="早退容許時間（分鐘）" hint="提前此時間內下班不計早退">
                      <input type="number" min={0} max={30} value={settings.earlyLeaveGraceMinutes} className="input-field w-24"
                        onChange={e => setSettings(s => ({ ...s, earlyLeaveGraceMinutes: Number(e.target.value) }))}
                        onBlur={() => save({ earlyLeaveGraceMinutes: settings.earlyLeaveGraceMinutes })} />
                    </FieldRow>

                    <FieldRow label="啟用早退扣薪">
                      <Toggle checked={settings.earlyLeaveDeductEnabled}
                        onChange={v => { setSettings(s => ({ ...s, earlyLeaveDeductEnabled: v })); save({ earlyLeaveDeductEnabled: v }); }} />
                    </FieldRow>

                    {settings.earlyLeaveDeductEnabled && (
                      <>
                        <FieldRow label="扣薪單位（每 N 分鐘）">
                          <input type="number" min={1} value={settings.earlyLeaveDeductUnit} className="input-field w-24"
                            onChange={e => setSettings(s => ({ ...s, earlyLeaveDeductUnit: Number(e.target.value) }))}
                            onBlur={() => save({ earlyLeaveDeductUnit: settings.earlyLeaveDeductUnit })} />
                        </FieldRow>
                        <FieldRow label="每單位扣薪金額（$）">
                          <input type="number" min={0} value={settings.earlyLeaveDeductAmount} className="input-field w-24"
                            onChange={e => setSettings(s => ({ ...s, earlyLeaveDeductAmount: Number(e.target.value) }))}
                            onBlur={() => save({ earlyLeaveDeductAmount: settings.earlyLeaveDeductAmount })} />
                        </FieldRow>
                      </>
                    )}

                    <LawNote>早退判定方式與遲到相同，以排班結束時間為基準。扣薪規則需明定於工作規則中。</LawNote>
                  </div>
                )}

                {/* 加班設定 */}
                {clockSubTab === 'overtime' && (
                  <div>
                    <FieldRow label="加班須事先申請">
                      <Toggle checked={settings.overtimeRequiresApproval}
                        onChange={v => { setSettings(s => ({ ...s, overtimeRequiresApproval: v })); save({ overtimeRequiresApproval: v }); }} />
                    </FieldRow>

                    <FieldRow label="每月加班上限（小時）" hint="勞基法 §32：一般 46hr，經勞資會議可至 54hr">
                      <input type="number" min={0} max={54} value={settings.overtimeMaxMonthly} className="input-field w-24"
                        onChange={e => setSettings(s => ({ ...s, overtimeMaxMonthly: Number(e.target.value) }))}
                        onBlur={() => save({ overtimeMaxMonthly: settings.overtimeMaxMonthly })} />
                    </FieldRow>

                    <FieldRow label="允許加班換補休" hint="勞基法 §32-1">
                      <Toggle checked={settings.overtimeToCompLeave}
                        onChange={v => { setSettings(s => ({ ...s, overtimeToCompLeave: v })); save({ overtimeToCompLeave: v }); }} />
                    </FieldRow>

                    {settings.overtimeToCompLeave && (
                      <FieldRow label="補休期限（天）" hint="未休完須折算工資">
                        <input type="number" min={1} max={365} value={settings.compLeaveDeadlineDays} className="input-field w-24"
                          onChange={e => setSettings(s => ({ ...s, compLeaveDeadlineDays: Number(e.target.value) }))}
                          onBlur={() => save({ compLeaveDeadlineDays: settings.compLeaveDeadlineDays })} />
                      </FieldRow>
                    )}

                    <LawNote>
                      勞基法 §32：延長工時須經勞工同意，每日含加班不超過 12 小時。
                      加班費率：前 2hr ×1.34、後 2hr ×1.67（§24）。
                      休息日加班：前 2hr ×1.34、2~8hr ×1.67、8~12hr ×2.67。
                      §32-1：加班可選擇補休，期限內未休完依原加班費率折算工資。
                    </LawNote>
                  </div>
                )}

                {/* 休息時間打卡 */}
                {clockSubTab === 'break' && (
                  <div>
                    <FieldRow label="休息時間需打卡">
                      <Toggle checked={settings.breakClockEnabled}
                        onChange={v => { setSettings(s => ({ ...s, breakClockEnabled: v })); save({ breakClockEnabled: v }); }} />
                    </FieldRow>

                    <FieldRow label="預設休息時長（分鐘）" hint="勞基法 §35：工作 4 小時至少休 30 分鐘">
                      <input type="number" min={30} max={120} value={settings.breakDurationMinutes} className="input-field w-24"
                        onChange={e => setSettings(s => ({ ...s, breakDurationMinutes: Number(e.target.value) }))}
                        onBlur={() => save({ breakDurationMinutes: settings.breakDurationMinutes })} />
                    </FieldRow>

                    <LawNote>
                      勞基法 §35：勞工繼續工作 4 小時，至少應有 30 分鐘之休息。
                      但實行輪班制或其工作有連續性或緊急性者，雇主得在工作時間內，另行調配其休息時間。
                    </LawNote>
                  </div>
                )}

                {/* Save indicator */}
                {saved && (
                  <div className="mt-4 text-sm text-sage-500 animate-fade-in">已儲存</div>
                )}
              </div>
            </div>
          )}

          {/* ── 門市資訊 ──────────────────────────────── */}
          {sideTab === 'shopInfo' && (
            <div className="bg-white rounded-card shadow-card p-6">
              <h2 className="text-lg font-bold text-morandi-800 mb-4">門市資訊</h2>

              <FieldRow label="店鋪名稱">
                <div className="text-sm text-morandi-700 font-medium">{settings.tenant?.name}</div>
              </FieldRow>
              <FieldRow label="店鋪代碼">
                <div className="text-sm text-morandi-400 font-mono">{settings.tenant?.slug}</div>
              </FieldRow>

              <div className="border-t border-morandi-100 my-4" />

              <FieldRow label="門市地址">
                <input value={settings.shopAddress || ''} className="input-field"
                  placeholder="例：台北市大安區..."
                  onChange={e => setSettings(s => ({ ...s, shopAddress: e.target.value }))}
                  onBlur={() => save({ shopAddress: settings.shopAddress })} />
              </FieldRow>

              <FieldRow label="門市電話">
                <input value={settings.shopPhone || ''} className="input-field w-48"
                  placeholder="02-xxxx-xxxx"
                  onChange={e => setSettings(s => ({ ...s, shopPhone: e.target.value }))}
                  onBlur={() => save({ shopPhone: settings.shopPhone })} />
              </FieldRow>

              <FieldRow label="營業時間">
                <input value={settings.businessHours || ''} className="input-field w-48"
                  placeholder="08:00-20:00"
                  onChange={e => setSettings(s => ({ ...s, businessHours: e.target.value }))}
                  onBlur={() => save({ businessHours: settings.businessHours })} />
              </FieldRow>

              <div className="border-t border-morandi-100 my-4" />

              <FieldRow label="WiFi SSID（打卡用）">
                <div className="text-sm text-morandi-500 font-mono">{settings.allowedWifiSSID || '未設定'}</div>
              </FieldRow>
              <FieldRow label="GPS 圍欄">
                <div className="text-sm text-morandi-500 break-all text-right min-w-0">
                  {settings.geoFenceLat && settings.geoFenceLng
                    ? `${settings.geoFenceLat}, ${settings.geoFenceLng}（半徑 ${settings.geoFenceRadiusM}m）`
                    : '未設定'}
                </div>
              </FieldRow>

              {isOwner && (
                <>
                  <div className="border-t border-morandi-100 my-4" />
                  {/* 不放進 FieldRow：左右兩欄在手機上放不下 6 位數與說明，改成上下排 */}
                  <div className="py-3 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-morandi-700">店內打卡站</div>
                        <div className="text-xs text-morandi-400 mt-0.5">一樓 Mac mini 或 iPad 開著，員工點名字輸入 PIN 就能打卡，不需要定位</div>
                      </div>
                      <button onClick={generateKioskCode}
                        className="px-4 py-2 rounded-btn text-sm font-medium text-white bg-sage-500 hover:bg-sage-600 transition-colors flex-shrink-0">
                        產生啟用碼
                      </button>
                    </div>
                    {kioskCode && (
                      <div className="bg-sage-50 border border-sage-200 rounded-btn p-4">
                        <div className="text-3xl font-mono font-bold tracking-[0.2em] text-sage-700 text-center">{kioskCode.code}</div>
                        <div className="text-xs text-morandi-500 mt-2 leading-relaxed break-all">
                          在店內裝置的瀏覽器開 <span className="font-mono">{window.location.origin}/kiosk</span>，輸入這 6 位數。
                          {new Date(kioskCode.expiresAt).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })} 前有效，只能用一次。
                        </div>
                      </div>
                    )}
                    {kioskCodeError && <div className="text-xs text-rose-500">{kioskCodeError}</div>}
                  </div>
                </>
              )}

              {saved && <div className="mt-4 text-sm text-sage-500 animate-fade-in">已儲存</div>}
            </div>
          )}

          {/* ── 法規數字（基本工資、勞健保費率）──────────── */}
          {sideTab === 'law' && <LawParamsPanel isOwner={isOwner} />}

          {/* ── 門市管理人員 ──────────────────────────── */}
          {sideTab === 'managers' && (
            <div className="bg-white rounded-card shadow-card p-6">
              <h2 className="text-lg font-bold text-morandi-800 mb-1">門市管理人員</h2>
              <p className="text-sm text-morandi-400 mb-4">管理人員擁有排班、簽核、查看報表的權限</p>

              <div className="space-y-3">
                {managers.map(m => (
                  <div key={m.id} className="flex items-center justify-between bg-morandi-50 rounded-btn px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-earth-200 flex items-center justify-center text-earth-600 text-sm font-medium">
                        {m.name.slice(0, 1)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-morandi-700">{m.name}</div>
                        <div className="text-xs text-morandi-400">{m.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-chip ${m.role === 'OWNER' ? 'bg-earth-200 text-earth-600' : 'bg-smoke-100 text-smoke-500'}`}>
                        {m.role === 'OWNER' ? '老闆' : '主管'}
                      </span>
                      {isOwner && m.role !== 'OWNER' && (
                        <button onClick={() => toggleManagerRole(m.id, m.role)}
                          className="text-xs text-rose-400 hover:text-rose-500 transition-colors">
                          移除主管
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {isOwner && (
                <div className="mt-4 text-xs text-morandi-300">
                  如需將員工升為主管，請至「員工名單」頁面編輯該員工的角色。
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────────

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-morandi-50 last:border-0">
      <div className="flex-shrink-0 mr-6">
        <div className="text-sm text-morandi-700">{label}</div>
        {hint && <div className="text-xs text-morandi-300 mt-0.5">{hint}</div>}
      </div>
      {/* min-w-0：手機上允許內容收窄換行（原本 flex-shrink-0 會把長字串撐出畫面） */}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${checked ? 'bg-sage-400' : 'bg-morandi-200'}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform
        ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function LawNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-6 bg-morandi-50 rounded-btn p-3 text-xs text-morandi-400 leading-relaxed">
      <span className="font-medium text-morandi-500">勞基法提醒：</span>{' '}{children}
    </div>
  );
}
